import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Button,
  Divider,
  IconButton,
} from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { styled, useTheme } from "@mui/material/styles";
import { useRouter } from "next/navigation";
import { FlagImage } from "@/lib/IranFlagIcon";
import ProfileAvatar from "@/app/components/shared/ProfileAvatar";
import SectionCard from "@/app/components/shared/SectionCard";
import {
  cleanProfessionalDisplayText,
  getSafePublicDisplayName,
  PUBLIC_MEMBER_NAME,
} from "@/lib/utils/nameUtils";
import { normalizeCandidateCountry } from "@/lib/search/rawCandidates";
import { useCallback, useEffect, useRef, useState } from "react";
import { getProfilePictureSources } from "@/lib/utils/profilePicture";

const ProfileCard = ({
  user,
  onViewProfile,
  compact = false,
  savedVariant = false,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const pictureSources = getProfilePictureSources(user, {
    requireFaceCheck: false,
  });
  const initialPicture =
    pictureSources.custom || pictureSources.cached || pictureSources.legacy || "";
  const [pictureSrc, setPictureSrc] = useState(initialPicture);
  const pictureRefreshAttempted = useRef(false);
  const profileId = user.id || user.sub || user.url;
  const shouldCleanPublicFields = Boolean(user.enriched);
  const displayTitle = user.title || "";
  const displayName = getSafePublicDisplayName(
    user.given_name,
    user.family_name,
    user.name,
  );
  const cardDisplayName =
    displayTitle && displayName !== PUBLIC_MEMBER_NAME
      ? `${displayTitle} ${displayName}`
      : displayName;

  const getCountryName = (countryCode) => {
    return normalizeCandidateCountry("", countryCode);
  };

  const getCompanyLinkData = () => {
    let companyData;

    if (
      user.company &&
      user.company.trim() !== "" &&
      user.company !== "No company" &&
      user.companyUrl &&
      user.companyUrl.trim() !== ""
    ) {
      companyData = {
        text: shouldCleanPublicFields
          ? cleanProfessionalDisplayText(user.company)
          : user.company,
        url: user.companyUrl,
        isCompany: true,
      };
    } else if (
      user.company &&
      user.company.trim() !== "" &&
      user.company !== "No company"
    ) {
      companyData = {
        text: shouldCleanPublicFields
          ? cleanProfessionalDisplayText(user.company)
          : user.company,
        url: null,
        isCompany: true,
      };
    } else {
      companyData = {
        text: "",
        url: null,
        isCompany: true,
      };
    }

    if (companyData.text.length > 20) {
      companyData.text = companyData.text.substring(0, 20) + "...";
    }

    return companyData;
  };

  const getProfileBadge = () => {
    // Enriched profiles are sourced from public information, not Meex members.
    if (user.enriched) {
      return { isPubliclySourced: true };
    }

    // For regular users: use historical response_rate
    const responseRate = user.responseRate;
    const requested = user.stats?.meeting_stats?.received?.requested || 0;

    // Check if new member
    if (requested < 3) {
      return {
        value: null,
        label: "response rate",
        isNewMember: true,
      };
    }

    // Use response_rate if available
    if (responseRate !== undefined && responseRate !== null) {
      return {
        value: responseRate,
        label: "response rate",
        isNewMember: false,
      };
    }

    // Fallback: calculate from stats during migration
    const dismissed = user.stats?.meeting_stats?.received?.dismissed || 0;
    if (requested > 0) {
      const calculated = Math.round(
        ((requested - dismissed) / requested) * 100
      );
      return {
        value: calculated,
        label: "response rate",
        isNewMember: false,
      };
    }

    // Final fallback for edge cases
    return {
      value: 95,
      label: "response rate",
      isNewMember: false,
    };
  };

  const companyData = getCompanyLinkData();
  const profileBadge = getProfileBadge();

  const CustomLinkedInIcon = styled(Box)(({ theme }) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary,
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: "800",
  }));

  const profileUrl = `/profile/${user.url || user.id || ""}`;

  const refreshPictureFromProfile = useCallback(async () => {
    if (!profileId || pictureRefreshAttempted.current) return;
    pictureRefreshAttempted.current = true;
    try {
      const response = await fetch(
        `/api/search/profile/${encodeURIComponent(profileId)}`,
      );
      if (!response.ok) return;
      const data = await response.json();
      const sources = getProfilePictureSources(data.profile, {
        requireFaceCheck: false,
      });
      setPictureSrc(sources.custom || sources.cached || sources.legacy || "");
    } catch {
      // Keep the initials fallback when the profile image cannot be refreshed.
    }
  }, [profileId]);

  useEffect(() => {
    pictureRefreshAttempted.current = false;
    setPictureSrc(initialPicture);
  }, [initialPicture, profileId]);

  useEffect(() => {
    if (!pictureSrc) refreshPictureFromProfile();
  }, [pictureSrc, refreshPictureFromProfile]);

  const handlePictureError = () => {
    if (pictureSrc === pictureSources.custom && pictureSources.cached) {
      setPictureSrc(pictureSources.cached);
    } else if (
      (pictureSrc === pictureSources.custom ||
        pictureSrc === pictureSources.cached) &&
      pictureSources.legacy
    ) {
      setPictureSrc(pictureSources.legacy);
    } else {
      setPictureSrc("");
      refreshPictureFromProfile();
    }
  };

  const handleCardClick = (e) => {
    e.preventDefault();
    router.push(profileUrl);
  };

  return (
    <a
      href={profileUrl}
      onClick={handleCardClick}
      style={{
        textDecoration: "none",
        display: "block",
        borderRadius: savedVariant ? 0 : "10px",
      }}
    >
      <Box
        sx={{
          cursor: "pointer",
          outline: "none",
          borderRadius: savedVariant ? 0 : "10px",
          transition: "background-color 0.2s ease, transform 0.2s ease",
          "&:hover .MuiPaper-root": {
            backgroundColor: savedVariant
              ? theme.palette.background.paper
              : theme.palette.action.hover,
            borderColor: savedVariant ? undefined : theme.palette.text.primary,
          },
          "&:hover": {
            transform: savedVariant ? "translateY(-2px)" : "none",
          },
        }}
      >
        <SectionCard
          mb={0}
          borderRadius={savedVariant ? 0 : "10px!important"}
          sx={{
            height: "490px",
            border: savedVariant
              ? "none"
              : `1px solid ${
                  theme.palette.mode === "dark"
                    ? theme.palette.divider
                    : theme.palette.grey[300]
                }`,
            borderTop: savedVariant
              ? `1px solid ${theme.palette.divider}`
              : undefined,
            borderBottom: savedVariant
              ? `1px solid ${theme.palette.divider}`
              : undefined,
            boxShadow:
              theme.palette.mode === "dark"
                ? undefined
                : "0 10px 26px rgba(15, 23, 42, 0.045)",
            transition:
              "border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
            pb: { xs: 4.5, sm: 3 },
          }}
        >
          <CardContent
            sx={{
              p: 3,
              px: compact ? 0.5 : 1.5,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <ProfileAvatar
                name={displayName}
                src={pictureSrc}
                onImageError={handlePictureError}
                onDragStart={(e) => e.preventDefault()}
                sx={{
                  mr: 2,
                  width: { xs: 96, sm: 80 },
                  height: { xs: 96, sm: 80 },
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  filter: user.enriched ? "grayscale(100%)" : "none",
                  userSelect: "none",
                  "& img": {
                    pointerEvents: "none",
                    userDrag: "none",
                    WebkitUserDrag: "none",
                  },
                }}
              />
              <Box>
                <Typography
                  variant="subtitle1"
                  title={cardDisplayName}
                  sx={{
                    fontFamily: theme.typography.h3.fontFamily,
                    fontWeight: 500,
                    fontSize: {
                      xs: "1.375rem",
                      sm: compact ? "1.250rem" : "1.375rem",
                    },
                    color: theme.palette.text.primary,
                    m: 0,
                    p: 0,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                  }}
                >
                  {cardDisplayName}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      opacity: 0.7,
                    }}
                    title={user.company}
                  >
                    {companyData.text}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {user.countryCode && (
                    <>
                      <Box
                        sx={{
                          mr: 1,
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "0px!important",
                        }}
                      >
                        <FlagImage
                          code={user.countryCode}
                          alt="Country Flag"
                          style={{
                            width: "25px",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.75rem",
                          mr: 1,
                        }}
                      >
                        {getCountryName(user.countryCode)}
                      </Typography>
                    </>
                  )}

                  {user.linkedinUrl && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        window.open(
                          user.linkedinUrl,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }}
                      size="small"
                      sx={{
                        color: theme.palette.grey[600],
                        "&:hover": {
                          color: theme.palette.grey[500],
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <CustomLinkedInIcon>in</CustomLinkedInIcon>
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ mt: 2, mb: 3, flex: 1 }}>
              <Box sx={{ mb: 3, flex: 1 }}>
                <Box sx={{ mt: 0.5, display: "flex", gap: 1 }}>
                  {profileBadge?.isPubliclySourced ? (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 500,
                      }}
                    >
                      Publicly Sourced
                    </Typography>
                  ) : profileBadge?.isNewMember ? (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 500,
                      }}
                    >
                      Verified Member
                    </Typography>
                  ) : profileBadge && profileBadge.value !== null ? (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 500,
                      }}
                    >
                      {profileBadge.value}% {profileBadge.label}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {user.expertise?.slice(0, 3).map((skill, idx) => (
                  <Chip
                    key={idx}
                    label={skill}
                    size="small"
                    sx={{
                      alignSelf: "flex-start",
                      px: 1,
                      backgroundColor: theme.palette.grey[200],
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                    }}
                  />
                ))}
                {user.expertise?.length > 3 && (
                  <Chip
                    label={`+${user.expertise.length - 3} more`}
                    size="small"
                    sx={{
                      alignSelf: "flex-start",
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                    }}
                  />
                )}
              </Box>
            </Box>

            <Divider sx={{ mx: -3 }} />
            <Button
              variant="text"
              fullWidth
              disableRipple
              onClick={() => {
                const idUrl = user.url || user.id || "";
                router.push(`/profile/${idUrl}`);
              }}
              sx={{
                fontWeight: 400,
                fontSize: "0.95rem",
                textTransform: "none",
                color: theme.palette.text.primary,
                justifyContent: "space-between",
                px: 0,
                pt: 1.5,
                "&:hover": {
                  backgroundColor: "transparent",
                  color: theme.palette.primary.main,
                },
                "&:active": {
                  backgroundColor: "transparent",
                },
              }}
              endIcon={
                <EastIcon
                  sx={{ fontSize: "1.25rem !important", opacity: 0.4 }}
                />
              }
            >
              Meet {user.given_name}
            </Button>
          </CardContent>
          <Typography
            variant="caption"
            sx={{
              pb: { xs: 3, sm: 1 },
              mt: 0,
              textAlign: "center",
              color: theme.palette.text.secondary,
              fontSize: "0.75rem",
              fontStyle: "italic",
              minHeight: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {user.enriched
              ? "Based on publicly available information"
              : "This profile is verified and up to date"}
          </Typography>
        </SectionCard>
      </Box>
    </a>
  );
};

export default ProfileCard;
